import { useEffect, useState } from "react";
import { getPaymentMode, getSaleUnlockToken } from "../services/api";

/**
 * THE answer to "can a paid plan be bought right now" — one fetch, one shape,
 * consumed by the pricing page, the signup form and the checkout.
 *
 * It exists because those three each used to decide for themselves and
 * disagreed: pricing.tsx carried a hardcoded `comingSoon: false` and
 * advertised Officer with a live CTA, while SignupPage asked the API, said
 * COMING SOON, and silently rewrote the visitor's paid plan back to Explorer.
 * Anything that renders or acts on plan availability reads this and nothing
 * else.
 *
 * The server enforces the same gate on create-order and create-subscription,
 * so this hook is presentation only — it cannot be the thing that keeps money
 * safe, and it is not asked to be.
 *
 * Fails closed: until the answer arrives, and on any error, `saleOpen` is
 * false. A network blip must never start selling.
 */
export interface SaleState {
  /** Can this visitor buy? False until resolved, and false on error. */
  saleOpen: boolean;
  /** Open to the general public (vs. opened just for us by ?unlock=). */
  publicSale: boolean;
  /** Razorpay is on a test key — charges are not real. */
  testMode: boolean;
  /** True while the answer is still in flight. */
  loading: boolean;
  /** We hold an unlock token and it is what opened the storefront. */
  unlocked: boolean;
}

export const useSaleOpen = (): SaleState => {
  const [state, setState] = useState<SaleState>({
    saleOpen: false,
    publicSale: false,
    testMode: false,
    loading: true,
    unlocked: false,
  });

  useEffect(() => {
    let mounted = true;
    const unlock = getSaleUnlockToken();

    getPaymentMode(unlock)
      .then((m) => {
        if (!mounted) return;
        setState({
          saleOpen: m.sale_open,
          publicSale: m.public_sale,
          testMode: m.mode === "test" && m.configured,
          loading: false,
          unlocked: m.sale_open && !m.public_sale,
        });
      })
      .catch(() => {
        if (!mounted) return;
        // Unreachable Mothership — no evidence the storefront is open.
        setState({
          saleOpen: false,
          publicSale: false,
          testMode: false,
          loading: false,
          unlocked: false,
        });
      });

    return () => {
      mounted = false;
    };
  }, []);

  return state;
};

export default useSaleOpen;
